import { Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { ViewProduct } from '../../../../pages/admin-dashboard/products/admin-products.model';
import { SafeUrl } from '@angular/platform-browser';
import { EmptyData } from '../../../../utils/empty-data/empty-data';
import { Router } from '@angular/router';
import { ToggleButton } from '../../../../shared/ui/toggle-button/toggle-button';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { finalize } from 'rxjs';
import { ProductService } from '../../../../services/product/product.service';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-admin-card-view',
  imports: [EmptyData, ToggleButton, ConfirmationDialog, SuccessfulNotification, NgClass],
  templateUrl: './admin-card-view.html',
  styleUrl: './admin-card-view.scss',
})
export class AdminCardView {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private productService = inject(ProductService);

  hasSubproducts = input<boolean | ''>();
  paginatedProducts = input.required<ViewProduct[]>();
  activeDropdownId = input();
  productImages = input<Map<string, { url: SafeUrl; isImage: boolean }>>();
  isFilterApplied = input();

  toggleDropdown = output<{ id: number; event: Event }>();
  openDeleteDialoug = output<{ product: ViewProduct }>();
  onViewProduct = output<{ productID: number }>();
  onEditProduct = output<{ productID: number }>();

  confirmationModal = signal<boolean>(false);
  type = signal<'deactivate' | 'activate' | null>(null);
  productIdToActivate = signal<number | null>(null);
  isSuccessNotification = signal<boolean>(false);
  errorActivation = signal<string>('');
  loadingActivation = signal<boolean>(false);

  deleteConfirmationModal = signal<boolean>(false);
  productIdToDelete = signal<number | null>(null);
  loadingDelete = signal<boolean>(false);
  errorDelete = signal<string>('');
  deleteSuccess = signal<boolean>(false);

  localActiveState = signal<Map<number, boolean>>(new Map());

  activeProductIds = computed(
    () =>
      new Set(
        this.paginatedProducts()
          .filter((p) => p.isActive)
          .map((p) => p.id),
      ),
  );

  onToggleDropDown(id: number, event: Event) {
    this.toggleDropdown.emit({ id, event });
  }

  onViewProductEmit(productId: number) {
    this.onViewProduct.emit({ productID: productId });
  }

  onEditProductEmit(productId: number) {
    this.onEditProduct.emit({ productID: productId });
  }

  onNavigateToEditProduct(e: Event, productId: number) {
    e.stopPropagation();
    this.router.navigate(['/admin/products/edit-product/', productId]);
  }

  onDeleteProduct(e: Event, productId: number) {
    e.stopPropagation();
    this.productIdToDelete.set(productId);
    this.errorDelete.set('');
    this.deleteConfirmationModal.set(true);
  }

  confirmDelete() {
    const productId = this.productIdToDelete();
    if (!productId) return;
    this.loadingDelete.set(true);
    this.productService
      .deleteProduct(productId)
      .pipe(finalize(() => this.loadingDelete.set(false)))
      .subscribe({
        next: () => {
          this.deleteConfirmationModal.set(false);
          this.deleteSuccess.set(true);
          this.openDeleteDialoug.emit({
            product: this.paginatedProducts().find((p) => p.id === productId)!,
          });
          setTimeout(() => this.deleteSuccess.set(false), 2000);
        },
        error: (err) => {
          const errorBody = err.error || err;
          const msgAr = errorBody?.messageAr || errorBody?.error?.messageAr;
          const msgEn = errorBody?.messageEn || errorBody?.error?.messageEn;
          this.errorDelete.set(
            this.isArabic()
              ? msgAr || 'لا يمكن حذف هذا المنتج حالياً'
              : msgEn || 'Cannot delete this product right now',
          );
        },
      });
  }

  onOpenDeleteDialoug(product: ViewProduct) {
    this.openDeleteDialoug.emit({ product });
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }

  isEnglish() {
    return this.locale.startsWith('en');
  }

  onNavigateToProduct(productId: string) {
    this.router.navigate(['/admin/products/view-product/', productId]);
  }

  ctaEmptyState() {
    this.router.navigate(['/admin/products/add-product']);
  }

  onActiveConfirmationModal(isActive: boolean, productId: number) {
    this.confirmationModal.set(true);
    this.type.set(isActive ? 'deactivate' : 'activate');
    this.productIdToActivate.set(productId);
  }

  isProductActive(productId: number): boolean {
    const local = this.localActiveState();
    if (local.has(productId)) return local.get(productId)!;
    return this.activeProductIds().has(productId);
  }

  onCancelConfirmationModal() {
    this.confirmationModal.set(false);
    this.type.set(null);
  }

  deactivateProduct() {
    const productId = this.productIdToActivate();
    if (!productId) return;
    this.loadingActivation.set(true);
    this.productService
      .deactivateProduct(productId)
      .pipe(finalize(() => this.loadingActivation.set(false)))
      .subscribe({
        next: () => {
          this.localActiveState.update((map) => new Map(map).set(productId, false));
          this.confirmationModal.set(false);
          this.isSuccessNotification.set(true);
          setTimeout(() => {
            this.type.set(null);
            this.isSuccessNotification.set(false);
          }, 1000);
        },
        error: (error) => {
          this.errorActivation.set(
            this.isArabic()
              ? error.messageAr || error.messageEn || 'حدث خطأ أثناء إلغاء تفعيل الصنف. الرجاء المحاولة مرة أخرى.'
              : error.messageEn ||
                  error.messageAr ||
                  'An error occurred while deactivating the product. Please try again.',
          );
        },
      });
  }

  activateProduct() {
    const productId = this.productIdToActivate();
    if (!productId) return;
    this.loadingActivation.set(true);
    this.productService
      .activateProduct(productId)
      .pipe(finalize(() => this.loadingActivation.set(false)))
      .subscribe({
        next: () => {
          this.localActiveState.update((map) => new Map(map).set(productId, true));
          this.confirmationModal.set(false);
          this.isSuccessNotification.set(true);
          setTimeout(() => {
            this.type.set(null);
            this.isSuccessNotification.set(false);
          }, 1000);
        },
        error: (error) => {
          this.errorActivation.set(
            this.isArabic()
              ? error.messageAr || error.messageEn || 'حدث خطأ أثناء تفعيل الصنف. الرجاء المحاولة مرة أخرى.'
              : error.messageEn ||
                  error.messageAr ||
                  'An error occurred while activating the product. Please try again.',
          );
        },
      });
  }

  confirmationModalContent = computed(() => {
    const ar = this.isArabic();
    const type = this.type();
    const loading = this.loadingActivation();

    const content = {
      deactivate: {
        title: ar ? 'تأكيد إلغاء التفعيل' : 'Confirm Deactivation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في إلغاء تفعيل هذا الصنف؟'
          : 'Are you sure you want to deactivate this product?',
        confirmContent: ar
          ? loading
            ? 'جارٍ إلغاء التفعيل...'
            : 'تأكيد إلغاء التفعيل'
          : loading
            ? 'Deactivating...'
            : 'Confirm Deactivation',
      },
      activate: {
        title: ar ? 'تأكيد تفعيل الصنف' : 'Confirm Product Activation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في تفعيل هذا الصنف؟ سيصبح الصنف مرئياً للعملاء ومتاحاً للشراء'
          : 'Are you sure you want to activate this product? The product will become visible to customers and available for purchase.',
        confirmContent: ar
          ? loading
            ? 'جارٍ التفعيل...'
            : 'تأكيد التفعيل'
          : loading
            ? 'Activating...'
            : 'Confirm Activation',
      },
    };

    return content[type!];
  });
}
